'use client';

import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth-store';
import { verifyOrganizationDomain } from '@/services/domain.service';
import {
  loginWithCredentials,
  loadSessionContext,
  verifyMfaLogin,
  resendMfaLoginEmail,
  AuthApiError,
  isMfaChallenge,
} from '@/services/auth.service';
import type { MfaChallengeResponse, MfaMethod } from '@/types/auth';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { ApiError } from '@/lib/api/client';
import { navigateHrmPage } from '@/lib/hrm-routing';
import ForgotPasswordFlow from '@/components/hrm/auth/ForgotPasswordFlow';
import { readSessionEndNotice } from '@/lib/auth/session-end';

export default function LoginPage() {
  const setAuthSession = useAuthStore((s) => s.setAuthSession);
  const setVerifiedInstance = useAuthStore(s => s.setVerifiedInstance);
  const verifiedInstance = useAuthStore(s => s.verifiedInstance);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [step, setStep] = useState<'domain' | 'credentials' | 'mfa'>('domain');
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallengeResponse | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaMethod, setMfaMethod] = useState<MfaMethod | 'backup'>('totp');
  const [mfaResendCooldown, setMfaResendCooldown] = useState(0);
  const [domain, setDomain] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isVerifyingDomain, setIsVerifyingDomain] = useState(false);
  const [domainVerified, setDomainVerified] = useState(false);
  const [domainError, setDomainError] = useState('');
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);

  useEffect(() => {
    setSessionNotice(readSessionEndNotice());
  }, []);

  useEffect(() => {
    if (mfaResendCooldown <= 0) return;
    const timer = window.setInterval(() => {
      setMfaResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [mfaResendCooldown]);

  const organizationLabel =
    verifiedInstance?.clients?.name?.trim() || domain.trim() || 'your organization';

  const orgCode =
    verifiedInstance?.auth_org_code?.trim() ||
    verifiedInstance?.clients?.domain_prefix?.trim() ||
    verifiedInstance?.code?.trim() ||
    domain.trim();

  const handleVerifyDomain = useCallback(async (): Promise<boolean> => {
    const trimmed = domain.trim();
    if (!trimmed) {
      setDomainError('Please enter your organization domain');
      setDomainVerified(false);
      setVerifiedInstance(null);
      return false;
    }

    if (trimmed.length < 3) {
      setDomainError('Domain must be at least 3 characters');
      setDomainVerified(false);
      setVerifiedInstance(null);
      return false;
    }

    setIsVerifyingDomain(true);
    setDomainError('');

    try {
      const instance = await verifyOrganizationDomain(trimmed);
      setVerifiedInstance(instance);
      setDomainVerified(true);
      setDomainError('');
      toast.success('Domain verified', {
        description: instance.clients?.name
          ? `Signed in to ${instance.clients.name}`
          : undefined,
      });
      return true;
    } catch (err) {
      setDomainVerified(false);
      setVerifiedInstance(null);
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Unable to verify domain';
      setDomainError(message === 'Invalid Domain' ? 'Organization domain not found' : message);
      return false;
    } finally {
      setIsVerifyingDomain(false);
    }
  }, [domain, setVerifiedInstance]);

  const handleDomainSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const verified = domainVerified ? true : await handleVerifyDomain();
      if (verified) {
        setStep('credentials');
      }
    },
    [domainVerified, handleVerifyDomain],
  );

  const handleBackToDomain = useCallback(() => {
    setStep('domain');
    setShowForgotPassword(false);
    setDomainError('');
    setDomainVerified(false);
    setVerifiedInstance(null);
    setMfaChallenge(null);
    setMfaCode('');
  }, [setVerifiedInstance]);

  const handleBackToCredentials = useCallback(() => {
    setStep('credentials');
    setMfaChallenge(null);
    setMfaCode('');
    setLoginError('');
  }, []);

  const handleOpenForgotPassword = useCallback(() => {
    if (!orgCode) {
      toast.error('Please verify your organization domain first');
      setStep('domain');
      return;
    }
    setShowForgotPassword(true);
    setLoginError('');
  }, [orgCode]);

  const handleBackToLogin = useCallback(() => {
    setShowForgotPassword(false);
    setLoginError('');
  }, []);

  const completeSignedIn = useCallback(
    async (accessToken: string, sessionId: string, deviceName?: string | null) => {
      localStorage.setItem('hrm-login-completed', '1');
      const { profile, products } = await loadSessionContext();
      setAuthSession({
        user: {
          id: profile.sub ?? profile.id,
          email: profile.email,
          firstName: profile.firstName ?? '',
          lastName: profile.lastName ?? '',
          orgId: profile.orgId,
          role: profile.role,
          isSuperAdmin: profile.isSuperAdmin,
          products,
          profileImageUrl: profile.profileImageUrl,
          coverImageUrl: profile.coverImageUrl,
        },
        accessToken,
        sessionId,
        orgCode,
        deviceName,
      });
      navigateHrmPage('dashboard');
      toast.success('Welcome back', {
        description: [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.email,
      });
    },
    [orgCode, setAuthSession],
  );

  const handleLogin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!orgCode) {
        setLoginError('Please verify your organization domain first');
        return;
      }
      if (!email.trim() || !password) {
        setLoginError('Email and password are required');
        return;
      }

      setIsLoggingIn(true);
      setLoginError('');

      try {
        const result = await loginWithCredentials({
          email: email.trim(),
          password,
          orgCode,
        });

        if (isMfaChallenge(result)) {
          setMfaChallenge(result);
          setMfaCode('');
          setMfaMethod(result.methods.includes('totp') ? 'totp' : 'email');
          setMfaResendCooldown(result.emailSent ? 120 : 0);
          setStep('mfa');
          toast.message('Additional verification required', {
            description: result.methods.includes('email')
              ? `Enter the code from your authenticator or the email sent to ${result.maskedEmail}`
              : 'Enter the 6-digit code from your authenticator app',
          });
          return;
        }

        await completeSignedIn(result.accessToken, result.sessionId, result.deviceName);
      } catch (err) {
        const message =
          err instanceof AuthApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Sign in failed';
        setLoginError(message);
        toast.error(message);
      } finally {
        setIsLoggingIn(false);
      }
    },
    [email, password, orgCode, completeSignedIn],
  );

  const handleMfaVerify = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!mfaChallenge || !orgCode) return;
      if (mfaCode.trim().length < 6) {
        setLoginError('Enter your verification code');
        return;
      }
      setIsLoggingIn(true);
      setLoginError('');
      try {
        const result = await verifyMfaLogin({
          mfaToken: mfaChallenge.mfaToken,
          code: mfaCode.trim(),
          orgCode,
          method: mfaMethod,
        });
        await completeSignedIn(result.accessToken, result.sessionId, result.deviceName);
      } catch (err) {
        const message =
          err instanceof AuthApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Verification failed';
        setLoginError(message);
        toast.error(message);
      } finally {
        setIsLoggingIn(false);
      }
    },
    [mfaChallenge, mfaCode, mfaMethod, orgCode, completeSignedIn],
  );

  const handleResendMfaEmail = useCallback(async () => {
    if (!mfaChallenge || mfaResendCooldown > 0 || isLoggingIn) return;
    setIsLoggingIn(true);
    setLoginError('');
    try {
      const result = await resendMfaLoginEmail(mfaChallenge.mfaToken);
      setMfaChallenge((prev) => (prev ? { ...prev, emailSent: true, maskedEmail: result.maskedEmail || prev.maskedEmail } : prev));
      setMfaResendCooldown(120);
      toast.success('Verification code sent', {
        description: result.maskedEmail ? `Sent to ${result.maskedEmail}` : undefined,
      });
    } catch (err) {
      const message =
        err instanceof AuthApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not resend code';
      setLoginError(message);
      toast.error(message);
    } finally {
      setIsLoggingIn(false);
    }
  }, [mfaChallenge, mfaResendCooldown, isLoggingIn]);

  if (showForgotPassword && orgCode) {
    return (
      <ForgotPasswordFlow
        orgCode={orgCode}
        organizationLabel={organizationLabel}
        initialEmail={email}
        onBackToLogin={handleBackToLogin}
        onBackToDomain={handleBackToDomain}
      />
    );
  }

  return (
    <div className="min-h-screen flex bg-white dark:bg-slate-950">
      {/* Step 1: Domain Input - Split Screen Layout */}
      {step === 'domain' && (
        <>
          {/* Left Side - Animated Background with Image */}
          <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden bg-gradient-to-br from-[var(--hrm-primary)] via-[var(--hrm-primary-dark)] to-[var(--hrm-primary-dark)]">
            {/* Animated Background Elements */}
            <div className="absolute inset-0">
              {/* Floating Circles */}
              <div className="absolute top-10 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse" />
              <div className="absolute bottom-20 right-20 w-96 h-96 bg-[var(--hrm-primary)]/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
              <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-[var(--hrm-primary)]/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '2s' }} />
              
              {/* Animated Grid Pattern */}
              <div 
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage: `
                    linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
                  `,
                  backgroundSize: '50px 50px',
                  animation: 'gridMove 20s linear infinite'
                }}
              />
              
              {/* Floating Shapes */}
              <div className="absolute top-20 right-40 w-20 h-20 border border-white/20 rounded-lg rotate-12 animate-bounce" style={{ animationDuration: '3s' }} />
              <div className="absolute bottom-40 left-20 w-16 h-16 bg-white/10 rounded-full animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }} />
              <div className="absolute top-1/3 right-1/4 w-12 h-12 border-2 border-white/30 rounded-lg -rotate-12 animate-bounce" style={{ animationDuration: '5s', animationDelay: '0.5s' }} />
              
              {/* Gradient Orbs */}
              <div className="absolute top-0 left-1/4 w-32 h-32 bg-gradient-to-r from-[#FF9F43]/30 to-transparent rounded-full blur-xl animate-ping" style={{ animationDuration: '4s' }} />
              <div className="absolute bottom-1/4 right-0 w-40 h-40 bg-gradient-to-l from-[#FFB870]/30 to-transparent rounded-full blur-xl animate-ping" style={{ animationDuration: '5s', animationDelay: '2s' }} />
            </div>

            {/* Content on Left Side */}
            <div className="relative z-10 flex flex-col justify-between p-12">
              {/* Logo */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <i className="ri-building-2-fill text-2xl text-white" />
                </div>
                <span className="text-xl font-bold text-white">HRM Enterprise</span>
              </div>

              {/* Main Illustration Area */}
              <div className="flex-1 flex items-center justify-center">
                <div className="relative">
                  {/* Central Illustration */}
                  <div className="w-80 h-80 relative">
                    {/* Background Glow */}
                    <div className="absolute inset-0 bg-white/10 rounded-full blur-3xl animate-pulse" />
                    
                    {/* Main Image Placeholder with Icon */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-64 h-64 bg-white/10 backdrop-blur-sm rounded-3xl border border-white/20 flex flex-col items-center justify-center gap-6 transform hover:scale-105 transition-transform duration-500">
                        <div className="w-24 h-24 bg-white/20 rounded-2xl flex items-center justify-center">
                          <i className="ri-team-fill text-5xl text-white" />
                        </div>
                        <div className="text-center px-6">
                          <p className="text-white/90 text-lg font-semibold">Streamline Your</p>
                          <p className="text-white/90 text-lg font-semibold">Workforce Management</p>
                        </div>
                        {/* Decorative Dots */}
                        <div className="flex gap-2">
                          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                        </div>
                      </div>
                    </div>
                    
                    {/* Orbiting Elements */}
                    <div className="absolute -top-4 -right-4 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center animate-spin" style={{ animationDuration: '8s' }}>
                      <i className="ri-calendar-check-line text-2xl text-white" />
                    </div>
                    <div className="absolute -bottom-4 -left-4 w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center animate-spin" style={{ animationDuration: '10s', animationDirection: 'reverse' }}>
                      <i className="ri-time-line text-xl text-white" />
                    </div>
                    <div className="absolute top-1/2 -right-8 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center animate-pulse">
                      <i className="ri-user-follow-line text-lg text-white" />
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Right Side - Domain Verification Card */}
          <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12 bg-slate-50 dark:bg-slate-950">
            <div className="w-full max-w-md animate-in fade-in slide-in-from-right-4 duration-500">
              {/* Mobile Logo - Only shown on small screens */}
              <div className="lg:hidden text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[var(--hrm-primary)] to-[var(--hrm-primary-dark)] rounded-2xl shadow-lg shadow-[var(--hrm-primary)]/25 mb-4">
                  <i className="ri-building-2-fill text-3xl text-white" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">HRM Enterprise</h1>
              </div>

              <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 p-8 border border-slate-100 dark:border-slate-800">
                {sessionNotice && (
                  <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40 px-3 py-2.5 text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
                    <i className="ri-information-line mt-0.5 shrink-0" />
                    <span>{sessionNotice}</span>
                  </div>
                )}
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-[var(--hrm-primary-light)] dark:bg-[var(--hrm-primary)]/10 rounded-xl mb-4">
                    <i className="ri-global-line text-2xl text-[var(--hrm-primary)]" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">Enter Organization Domain</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    Enter your company domain to continue to your workspace
                  </p>
                </div>

                <form onSubmit={handleDomainSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Organization Domain
                    </label>
                    <div className="relative">
                      <i className="ri-earth-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        value={domain}
                        onChange={(e) => {
                          setDomain(e.target.value);
                          setDomainError('');
                          setDomainVerified(false);
                          setVerifiedInstance(null);
                        }}
                        onBlur={() => {
                          if (domain.trim().length >= 3) {
                            void handleVerifyDomain();
                          }
                        }}
                        placeholder="your-company"
                        disabled={isVerifyingDomain}
                        autoComplete="organization"
                        className="w-full pl-11 pr-12 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--hrm-primary)]/20 focus:border-[var(--hrm-primary)] transition-all disabled:opacity-60"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center">
                        {isVerifyingDomain ? (
                          <i className="ri-loader-4-line animate-spin text-slate-400" />
                        ) : domainVerified ? (
                          <i className="ri-checkbox-circle-fill text-emerald-500 text-lg" />
                        ) : domainError && domain.trim() ? (
                          <i className="ri-close-circle-fill text-red-500 text-lg" />
                        ) : null}
                      </span>
                    </div>
                    {domainError && (
                      <p className="mt-2 text-sm text-red-500 flex items-center gap-1.5">
                        <i className="ri-error-warning-line" />
                        {domainError}
                      </p>
                    )}
                  </div>

                  {domainVerified && verifiedInstance?.clients?.name && (
                    <p className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                      <i className="ri-building-line" />
                      {verifiedInstance.clients.name}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={isVerifyingDomain}
                    className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-[var(--hrm-primary)] to-[var(--hrm-primary-dark)] hover:from-[var(--hrm-primary-dark)] hover:to-[var(--hrm-primary-dark)] text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-[var(--hrm-primary)]/25 hover:shadow-[var(--hrm-primary)]/40 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isVerifyingDomain ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Verifying domain...
                      </>
                    ) : (
                      <>
                        Continue to Workspace
                        <i className="ri-arrow-right-line" />
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-8 pt-6 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-xs text-center text-slate-400 dark:text-slate-500">
                    Don't know your domain?{' '}
                    <button type="button" className="text-[var(--hrm-primary)] hover:text-[var(--hrm-primary-dark)] dark:text-[#FF9F43] dark:hover:text-[#FFB870] font-medium">
                      Contact administrator
                    </button>
                  </p>
                </div>

                {/* Quick Hints */}
                <div className="mt-6 flex items-center justify-center gap-4 text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-shield-check-line text-emerald-500" />
                    Secure SSL
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-lock-password-line text-emerald-500" />
                    Encrypted
                  </span>
                </div>
              </div>

              {/* Footer */}
              <p className="text-center text-xs text-slate-400 mt-6">
                © 2026 HRM Enterprise. All rights reserved.
              </p>
            </div>
          </div>
        </>
      )}

      {/* Step 2: Credentials - Split Screen Layout */}
      {step === 'credentials' && (
        <>
          {/* Left Side - Animated Background with Sign In Theme */}
          <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden bg-gradient-to-br from-[var(--hrm-primary)] via-[var(--hrm-primary-dark)] to-[var(--hrm-primary-dark)]">
            {/* Animated Background Elements */}
            <div className="absolute inset-0">
              {/* Floating Circles */}
              <div className="absolute top-20 right-20 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-pulse" />
              <div className="absolute bottom-10 left-10 w-72 h-72 bg-[var(--hrm-primary)]/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
              <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-[var(--hrm-primary)]/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '2s' }} />
              
              {/* Animated Grid Pattern */}
              <div 
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage: `
                    linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
                  `,
                  backgroundSize: '50px 50px',
                  animation: 'gridMove 20s linear infinite'
                }}
              />
              
              {/* Floating Shapes */}
              <div className="absolute top-32 left-40 w-24 h-24 border-2 border-white/20 rounded-2xl rotate-45 animate-bounce" style={{ animationDuration: '4s' }} />
              <div className="absolute bottom-32 right-32 w-16 h-16 bg-white/10 rounded-xl animate-bounce" style={{ animationDuration: '3s', animationDelay: '0.5s' }} />
              <div className="absolute top-1/2 left-20 w-12 h-12 border border-white/30 rounded-full animate-bounce" style={{ animationDuration: '5s', animationDelay: '1s' }} />
              
              {/* Gradient Orbs */}
              <div className="absolute bottom-0 right-1/3 w-40 h-40 bg-gradient-to-t from-[#FF9F43]/30 to-transparent rounded-full blur-xl animate-ping" style={{ animationDuration: '4s' }} />
              <div className="absolute top-1/4 left-0 w-32 h-32 bg-gradient-to-r from-[#FFB870]/30 to-transparent rounded-full blur-xl animate-ping" style={{ animationDuration: '5s', animationDelay: '2s' }} />
            </div>

            {/* Content on Left Side */}
            <div className="relative z-10 flex flex-col justify-between p-12">
              {/* Logo with Back Button */}
              <div className="flex items-center gap-4">
                <button
                  onClick={handleBackToDomain}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl text-white transition-colors"
                >
                  <i className="ri-arrow-left-line" />
                  <span className="text-sm font-medium">Back</span>
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                    <i className="ri-building-2-fill text-xl text-white" />
                  </div>
                  <span className="text-lg font-bold text-white">HRM Enterprise</span>
                </div>
              </div>

              {/* Main Illustration Area - Sign In Theme */}
              <div className="flex-1 flex items-center justify-center">
                <div className="relative">
                  {/* Central Illustration */}
                  <div className="w-80 h-80 relative">
                    {/* Background Glow */}
                    <div className="absolute inset-0 bg-white/10 rounded-full blur-3xl animate-pulse" />
                    
                    {/* Main Sign In Illustration */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-64 h-64 bg-white/10 backdrop-blur-sm rounded-3xl border border-white/20 flex flex-col items-center justify-center gap-6 transform hover:scale-105 transition-transform duration-500">
                        {/* Shield/Security Icon */}
                        <div className="w-24 h-24 bg-white/20 rounded-2xl flex items-center justify-center relative">
                          <i className="ri-shield-user-fill text-5xl text-white" />
                          {/* Checkmark badge */}
                          <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-emerald-400 rounded-full flex items-center justify-center animate-bounce" style={{ animationDuration: '2s' }}>
                            <i className="ri-check-line text-white text-lg" />
                          </div>
                        </div>
                        <div className="text-center px-6">
                          <p className="text-white/90 text-lg font-semibold">Secure Access</p>
                          <p className="text-white/70 text-sm mt-1">to your workspace</p>
                        </div>
                        {/* Animated Dots */}
                        <div className="flex gap-2">
                          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                        </div>
                      </div>
                    </div>
                    
                    {/* Orbiting Elements - Security themed */}
                    <div className="absolute -top-4 -right-4 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center animate-spin" style={{ animationDuration: '8s' }}>
                      <i className="ri-lock-password-line text-2xl text-white" />
                    </div>
                    <div className="absolute -bottom-4 -left-4 w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center animate-spin" style={{ animationDuration: '10s', animationDirection: 'reverse' }}>
                      <i className="ri-fingerprint-line text-xl text-white" />
                    </div>
                    <div className="absolute top-1/2 -right-8 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center animate-pulse">
                      <i className="ri-shield-check-line text-lg text-white" />
                    </div>
                    <div className="absolute top-1/4 -left-8 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center animate-ping" style={{ animationDuration: '3s' }}>
                      <i className="ri-key-line text-sm text-white" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Text - Domain Info */}
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                  <i className="ri-global-line text-white/80" />
                  <span className="text-white/90 text-sm font-medium">{organizationLabel}</span>
                </div>
                <p className="text-white/70 text-sm max-w-sm">
                  Sign in securely to access your organization's HRM dashboard and manage your workforce efficiently.
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Sign In Card */}
          <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12 bg-slate-50 dark:bg-slate-950">
            <div className="w-full max-w-md animate-in fade-in slide-in-from-right-4 duration-500">
              {/* Mobile Header - Only shown on small screens */}
              <div className="lg:hidden mb-6">
                <button
                  onClick={handleBackToDomain}
                  className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 mb-4"
                >
                  <i className="ri-arrow-left-line" />
                  Back to domain
                </button>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-[var(--hrm-primary-light)] dark:bg-[var(--hrm-primary)]/10 rounded-xl mb-3">
                    <i className="ri-global-line text-2xl text-[var(--hrm-primary)]" />
                  </div>
                  <p className="text-sm text-slate-500">{organizationLabel}</p>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 p-8 border border-slate-100 dark:border-slate-800">
                {sessionNotice && (
                  <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40 px-3 py-2.5 text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
                    <i className="ri-information-line mt-0.5 shrink-0" />
                    <span>{sessionNotice}</span>
                  </div>
                )}
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-[var(--hrm-primary)] to-[var(--hrm-primary-dark)] rounded-xl mb-4 shadow-lg shadow-[var(--hrm-primary)]/25">
                    <i className="ri-login-circle-line text-2xl text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">Welcome Back</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    Sign in to continue to your workspace
                  </p>
                </div>

                {loginError && (
                  <p className="mb-4 text-sm text-red-500 flex items-center gap-1.5">
                    <i className="ri-error-warning-line" />
                    {loginError}
                  </p>
                )}

                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setLoginError('');
                        }}
                        placeholder="you@company.com"
                        required
                        autoComplete="email"
                        disabled={isLoggingIn}
                        className="w-full pl-11 pr-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--hrm-primary)]/20 focus:border-[var(--hrm-primary)] transition-all disabled:opacity-60"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <i className="ri-lock-password-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value.trim());
                          setLoginError('');
                        }}
                        placeholder="Enter your password"
                        required
                        autoComplete="current-password"
                        disabled={isLoggingIn}
                        className="w-full pl-11 pr-12 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--hrm-primary)]/20 focus:border-[var(--hrm-primary)] transition-all disabled:opacity-60"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        disabled={isLoggingIn}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors disabled:opacity-50"
                      >
                        <i className={showPassword ? 'ri-eye-off-line text-lg' : 'ri-eye-line text-lg'} />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="remember"
                        className="w-4 h-4 rounded accent-[var(--hrm-primary)]"
                      />
                      <label htmlFor="remember" className="text-sm text-slate-600 dark:text-slate-400">
                        Remember me
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={handleOpenForgotPassword}
                      className="text-sm text-[var(--hrm-primary)] hover:text-[var(--hrm-primary-dark)] dark:text-[#FF9F43] dark:hover:text-[#FFB870] font-medium"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoggingIn}
                    className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-[var(--hrm-primary)] to-[var(--hrm-primary-dark)] hover:from-[var(--hrm-primary-dark)] hover:to-[var(--hrm-primary-dark)] text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-[var(--hrm-primary)]/25 hover:shadow-[var(--hrm-primary)]/40 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isLoggingIn ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      <>
                        <i className="ri-login-box-line" />
                        Sign In
                      </>
                    )}
                  </button>
                </form>

              </div>

              {/* Footer */}
              <p className="text-center text-xs text-slate-400 mt-6">
                Protected by industry-standard security
              </p>
            </div>
          </div>
        </>
      )}

      {step === 'mfa' && mfaChallenge && (
        <div className="w-full flex items-center justify-center p-6 lg:p-12 bg-slate-50 dark:bg-slate-950">
          <div className="w-full max-w-md">
            <button
              type="button"
              onClick={handleBackToCredentials}
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 mb-4"
            >
              <i className="ri-arrow-left-line" />
              Back to password
            </button>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-8 border border-slate-100 dark:border-slate-800">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-[var(--hrm-primary-light)] dark:bg-[var(--hrm-primary)]/10 rounded-xl mb-4">
                  <i className="ri-shield-keyhole-line text-2xl text-[var(--hrm-primary)]" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Verify it is you</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                  {mfaChallenge.methods.includes('email')
                    ? `Enter the authenticator code or the email we sent to ${mfaChallenge.maskedEmail}`
                    : 'Enter the 6-digit code from your authenticator app'}
                </p>
              </div>

              {loginError && (
                <p className="mb-4 text-sm text-red-500 flex items-center gap-1.5">
                  <i className="ri-error-warning-line" />
                  {loginError}
                </p>
              )}

              <form onSubmit={handleMfaVerify} className="space-y-5">
                {(mfaChallenge.methods.length > 1 || mfaChallenge.methods.includes('totp')) && (
                  <div className="flex rounded-xl bg-slate-100 dark:bg-slate-800 p-1">
                    {mfaChallenge.methods.map((method) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => setMfaMethod(method)}
                        className={`flex-1 py-2 text-xs font-semibold rounded-lg ${
                          mfaMethod === method
                            ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                            : 'text-slate-500'
                        }`}
                      >
                        {method === 'totp' ? 'Authenticator' : 'Email'}
                      </button>
                    ))}
                    {mfaChallenge.methods.includes('totp') && (
                      <button
                        type="button"
                        onClick={() => setMfaMethod('backup')}
                        className={`flex-1 py-2 text-xs font-semibold rounded-lg ${
                          mfaMethod === 'backup'
                            ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                            : 'text-slate-500'
                        }`}
                      >
                        Backup
                      </button>
                    )}
                  </div>
                )}

                {mfaMethod === 'backup' ? (
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={mfaCode}
                    onChange={(e) => {
                      setMfaCode(e.target.value.replace(/\s/g, ''));
                      setLoginError('');
                    }}
                    placeholder="Backup code"
                    maxLength={8}
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm tracking-widest"
                  />
                ) : (
                  <div className="flex justify-center">
                    <InputOTP maxLength={6} value={mfaCode} onChange={setMfaCode}>
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                      </InputOTPGroup>
                      <InputOTPSeparator />
                      <InputOTPGroup>
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                )}

                {mfaChallenge.methods.includes('email') && mfaMethod !== 'backup' && (
                  <button
                    type="button"
                    disabled={mfaResendCooldown > 0 || isLoggingIn}
                    onClick={() => void handleResendMfaEmail()}
                    className="w-full text-sm text-[var(--hrm-primary)] font-medium disabled:text-slate-400"
                  >
                    {mfaResendCooldown > 0
                      ? `Resend email code in ${mfaResendCooldown}s`
                      : 'Resend email code'}
                  </button>
                )}

                <button
                  type="submit"
                  disabled={isLoggingIn}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-[var(--hrm-primary)] to-[var(--hrm-primary-dark)] text-white font-semibold rounded-xl disabled:opacity-60"
                >
                  {isLoggingIn ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Continue'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
